import { createApi } from "@reduxjs/toolkit/query/react";
import customFetchBase from "./customFetchBase";
import { getQueryParams } from "utils/getQueryParams";
import {
  IGenericResponse,
  IPaginatedList,
  IPaginationQuery,
} from "Interfaces/PaginatedList";
import { ICompany } from "Models/Company";
import { CompaniesFilters } from "Interfaces/CompaniesFilters";

export const companyApi = createApi({
  reducerPath: "companyApi",
  baseQuery: customFetchBase,
  tagTypes: ["Company"],
  keepUnusedDataFor: 300,  // Keep cached data for 5 minutes globally
  refetchOnReconnect: true, // Refetch data when reconnected to the network
  refetchOnFocus: true, // Refetch data when the window is focused again
  endpoints: (builder) => ({
    getCompanies: builder.query<IPaginatedList<ICompany>, IPaginationQuery<CompaniesFilters>>({
      query(queryFilters) {

        return {
          url: getQueryParams(`company`, queryFilters),
          credentials: "include",
        };
      },
      transformResponse: (results: IGenericResponse<IPaginatedList<ICompany>>) =>
        results.data,
      providesTags: ["Company"],
    }),
    getAllCompanies: builder.query<ICompany[], void>({
      query() {
        return {
          url: "company/get/all",
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: () => [{ type: "Company" }],
      transformResponse: (results: { data: ICompany[] }) => {
        return results.data;
      },
    }),
    // Get single company

    getACompany: builder.query<ICompany, string>({
      query(companyId) {
        
        return {
          url: "company/" + companyId,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["Company"],
      transformResponse: (result: { data: ICompany }) => {
        return result.data;
      },
    }),

    // Post company

    postCompany: builder.mutation<ICompany, FormData>({
      query(newCompany) {
        return {
          url: "company",
          method: "POST",
          body: newCompany,
          credentials: "include",
        };
      },
      invalidatesTags: ["Company"],
      transformResponse: (result: { data:  ICompany }) => {
        return result.data;
      },
    }),

    // Update company

    updateCompany: builder.mutation<
      ICompany,
      { id: string; newsToUpdate: FormData }
    >({
      query({ id: companyId, newsToUpdate: updatedCompany }) {
        return {
          url: "company/" + companyId,
          method: "PUT",
          body: updatedCompany,
          credentials: "include",
        };
      },
      invalidatesTags: ["Company"],
      transformResponse: (result: { data: ICompany }) => {
        return result.data;
      },
    }),

    // Delete company

    deleteCompany: builder.mutation<ICompany, string>({
      query(companyId) {
        return {
          url: "company/" + companyId,
          method: "DELETE",
          credentials: "include",
        };
      },
      invalidatesTags: ["Company"],
      transformResponse: (result: { data: ICompany }) => {
        return result.data;
      },
    }),
  }),
});

export const {
  useGetACompanyQuery,
  useGetCompaniesQuery,
  usePostCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetAllCompaniesQuery,
} = companyApi;
